import { useState, useEffect, useCallback, useMemo } from 'react';
import { DetailModal } from '../../components/ui/DetailModal';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Combobox } from '../../components/ui/Combobox';
import { Spinner } from '../../components/ui/Spinner';
import { CalendarPicker } from '../../components/filters/CalendarPicker';
import { pricingApi } from '../../lib/pricingApi';
import { formatIsraelDate, formatIsraelDayTime } from '../../utils/formatters';
import { moneyLabel, SYMBOL, CURRENCIES, CURRENCY_LABEL, DEFAULT_CURRENCY, STATUSES, STATUS_TONE } from './helpers';

const field = 'w-full rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-brand/40 dark:border-white/15 dark:bg-slate-800 dark:text-slate-100';
const label = 'block mb-1 text-xs font-medium text-slate-500 dark:text-slate-400';

// One editable reseller price. Kept as its own component so each row owns its
// draft state and its own save — editing reseller B never re-renders or
// discards a half-typed figure for reseller A.
function PriceRow({ row, subProduct, onSaved, onRemoved, onError, onDiscardDraft }) {
  const [price, setPrice] = useState(row.price === null ? '' : String(row.price));
  // Not editable here: a reseller is priced in ONE currency, chosen once beside
  // their name on the pricing page. Letting a single sub-product differ is how
  // a reseller ends up with half a list in shekels.
  const currency = row.currency || subProduct.currency || DEFAULT_CURRENCY;
  const [from, setFrom] = useState(row.effectiveFrom || '');
  const [to, setTo] = useState(row.effectiveTo || '');
  const [busy, setBusy] = useState(false);

  // A reload replaces the row objects; re-sync so a saved value doesn't sit
  // looking unsaved.
  useEffect(() => {
    setPrice(row.price === null ? '' : String(row.price));
    setFrom(row.effectiveFrom || '');
    setTo(row.effectiveTo || '');
  }, [row]);

  const dirty = String(price).trim() !== (row.price === null ? '' : String(row.price))
    || (from || '') !== (row.effectiveFrom || '')
    || (to || '') !== (row.effectiveTo || '');

  // "Same as base price": for the common case where a reseller is simply
  // charged the list figure, the manager confirms it instead of retyping it.
  // Derived from the box rather than stored, so typing a different figure
  // unticks it on its own and the two can never disagree.
  const base = subProduct.basePrice;
  const baseCurrency = subProduct.currency || DEFAULT_CURRENCY;
  const hasBase = base !== null && base !== undefined && base !== '';
  const matchesBase = hasBase && String(price).trim() !== '' && Number(price) === Number(base);

  // Copies the FIGURE. Nothing is converted, so in a reseller priced in ILS a
  // base price of 40 becomes ₪40 — which is what the label beside the box says.
  const toggleBase = (on) => setPrice(on ? String(base) : '');

  const save = async () => {
    if (String(price).trim() === '') { onError('Enter a price, or remove this reseller’s pricing.'); return; }
    setBusy(true);
    try {
      await pricingApi.setPrice(subProduct.id, {
        resellerName: row.reseller,
        price: Number(price),
        currency,
        effectiveFrom: from || null,
        effectiveTo: to || null,
        // What this row was showing when the user started typing. The server
        // rejects the save if it no longer matches, rather than overwriting a
        // change someone else made in the meantime.
        expectedPrice: row.price,
      });
      onSaved(row.reseller);
    } catch (e) {
      onError(e.message);
    } finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await pricingApi.clearPrice(subProduct.id, row.reseller);
      onRemoved(row.reseller);
    } catch (e) {
      onError(e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className={`rounded-xl border p-3 transition-colors ${dirty
      ? 'border-amber-300 bg-amber-50/70 dark:border-amber-500/40 dark:bg-amber-500/10'
      : 'border-slate-200 bg-white/60 dark:border-white/10 dark:bg-white/5'}`}
    >
      <div className="mb-2.5 flex items-center justify-between gap-2">
        <span className="truncate text-sm font-semibold text-navy-800 dark:text-slate-100">{row.reseller}</span>
        <div className="flex shrink-0 items-center gap-2">
          {dirty && !row.isNew && <Badge tone="down">Unsaved</Badge>}
          {row.isNew
            ? <Badge tone="brand">New</Badge>
            : <span className="text-xs text-slate-400 dark:text-slate-500">{moneyLabel(row.price, currency)}</span>}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <div>
          <span className={label}>Price</span>
          <div className="relative">
            <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-slate-400">
              {SYMBOL[currency] || '$'}
            </span>
            <input
              type="number" step="0.01" min="0" inputMode="decimal"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="0.00"
              className={`${field} no-spin pl-6 text-right tabular-nums`}
            />
          </div>
        </div>
        <div>
          <span className={label}>Currency</span>
          <div
            title={`${row.reseller} is priced in ${currency}. Change it beside their name on the Product Pricing page — it applies to every price they have.`}
            className={`${field} flex cursor-default items-center justify-between bg-slate-50 text-slate-500 dark:bg-white/5 dark:text-slate-400`}
          >
            <span className="font-medium">{SYMBOL[currency]} {CURRENCY_LABEL[currency] || currency}</span>
            <span className="text-[10px] uppercase tracking-wide text-slate-400 dark:text-slate-500">reseller</span>
          </div>
        </div>
        <div>
          <span className={label}>Effective from</span>
          {/* portal: the dialog body scrolls, and an absolutely positioned
              popover would be clipped by its overflow. */}
          <CalendarPicker value={from} onChange={setFrom} portal placeholder="Any date" clearLabel="Clear" />
        </div>
        <div>
          <span className={label}>Effective to</span>
          <CalendarPicker value={to} onChange={setTo} portal placeholder="No end date" clearLabel="Clear" />
        </div>
      </div>

      <div className="mt-2.5 flex flex-wrap items-center justify-end gap-2">
        {hasBase && (
          <label className="mr-auto inline-flex cursor-pointer select-none items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
            <input
              type="checkbox"
              checked={matchesBase}
              onChange={(e) => toggleBase(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-slate-300 text-brand focus:ring-2 focus:ring-brand/40 dark:border-white/20 dark:bg-slate-800"
            />
            Same as base price ({moneyLabel(base, currency)}
            {currency !== baseCurrency && <span className="text-slate-400 dark:text-slate-500"> — base is {moneyLabel(base, baseCurrency)}, not converted</span>})
          </label>
        )}
        <button
          type="button"
          onClick={row.isNew ? () => onDiscardDraft(row.reseller) : remove}
          disabled={busy}
          className="rounded-lg px-2 py-1 text-xs font-medium text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50 dark:text-slate-500 dark:hover:bg-red-500/15 dark:hover:text-red-300"
        >
          {row.isNew ? 'Cancel' : 'Remove'}
        </button>
        <Button onClick={save} disabled={busy || !dirty} className="px-3 py-1.5 text-xs">
          {busy ? 'Saving…' : row.isNew ? 'Add price' : 'Save price'}
        </Button>
      </div>
    </div>
  );
}

export function SubProductPanel({ subProductId, open, onClose, canEdit, resellers, focusReseller, focusCurrency, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [ok, setOk] = useState(null);
  const [drafts, setDrafts] = useState([]);       // resellers being added, not yet saved
  const [details, setDetails] = useState(null);   // editable copy of the sub-product fields
  const [savingDetails, setSavingDetails] = useState(false);

  const load = useCallback(() => {
    if (!subProductId) return;
    setLoading(true);
    pricingApi.subProduct(subProductId)
      .then((d) => {
        setDetail(d);
        setDetails({
          name: d.name, sku: d.sku || '', status: d.status,
          effectiveDate: d.effectiveDate || '', basePrice: d.basePrice === null ? '' : String(d.basePrice),
          currency: d.currency, notes: d.notes || '',
        });
        // The reseller picked on the page behind is why the dialog was opened.
        // If they have no price here yet, open their row ready to fill in
        // rather than making the manager pick the same reseller a second time.
        const needsRow = canEdit && focusReseller
          && !d.resellerPrices.some((r) => r.reseller.toLowerCase() === focusReseller.toLowerCase());
        setDrafts(needsRow ? [{
          // In the currency that reseller is priced in on the page behind, so a
          // price added here matches the rest of their list.
          reseller: focusReseller, price: null, currency: focusCurrency || d.currency,
          effectiveFrom: '', effectiveTo: '', isNew: true,
        }] : []);
      })
      .catch((e) => setErr(e.message))
      .finally(() => setLoading(false));
  }, [subProductId, canEdit, focusReseller, focusCurrency]);

  useEffect(() => { if (open) { setErr(null); setOk(null); load(); } }, [open, load]);

  const flash = (text) => { setOk(text); setErr(null); setTimeout(() => setOk(null), 3000); };
  const afterWrite = (msg) => { flash(msg); load(); onChanged?.(); };

  // Only the Details block is saved by the footer button — reseller prices are
  // separate records with their own row action — so the button must light up for
  // changes to these fields alone.
  const detailsDirty = useMemo(() => {
    if (!detail || !details) return false;
    return details.name !== detail.name
      || details.sku !== (detail.sku || '')
      || details.status !== detail.status
      || details.effectiveDate !== (detail.effectiveDate || '')
      || details.basePrice !== (detail.basePrice === null ? '' : String(detail.basePrice))
      || details.currency !== detail.currency
      || details.notes !== (detail.notes || '');
  }, [detail, details]);

  const addDraft = (name) => {
    if (!name) return;
    const taken = detail.resellerPrices.some((r) => r.reseller.toLowerCase() === name.toLowerCase())
      || drafts.some((d) => d.reseller.toLowerCase() === name.toLowerCase());
    if (taken) { setErr(`${name} already has pricing on this sub-product.`); return; }
    setErr(null);
    setDrafts((p) => [...p, {
      reseller: name,
      price: null,
      // Their own currency is only known on the pricing page, where it is set;
      // a row added here starts from the sub-product's base currency.
      currency: name.toLowerCase() === String(focusReseller || '').toLowerCase()
        ? (focusCurrency || detail.currency)
        : detail.currency,
      effectiveFrom: '', effectiveTo: '', isNew: true,
    }]);
  };

  const saveDetails = async () => {
    setSavingDetails(true);
    try {
      await pricingApi.updateSubProduct(subProductId, {
        name: details.name,
        sku: details.sku || null,
        status: details.status,
        effectiveDate: details.effectiveDate || null,
        basePrice: details.basePrice === '' ? null : Number(details.basePrice),
        currency: details.currency,
        notes: details.notes || null,
      });
      afterWrite('Details saved.');
    } catch (e) { setErr(e.message); } finally { setSavingDetails(false); }
  };

  // Resellers with no pricing yet — the only ones worth offering in the picker.
  const available = detail
    ? resellers.filter((r) => !detail.resellerPrices.some((p) => p.reseller.toLowerCase() === r.toLowerCase())
      && !drafts.some((d) => d.reseller.toLowerCase() === r.toLowerCase()))
    : [];

  const isFocus = (name) => Boolean(focusReseller)
    && String(name).toLowerCase() === focusReseller.toLowerCase();

  // This dialog is about the reseller selected on the page behind. Only their
  // row is shown: another reseller's negotiated price is not what you opened it
  // to see, and having it in reach is how the wrong one gets edited. Switch
  // reseller on the page to work on theirs.
  const priceRows = detail
    ? (focusReseller
      ? detail.resellerPrices.filter((r) => isFocus(r.reseller))
      : [...detail.resellerPrices].sort((a, b) => a.reseller.localeCompare(b.reseller)))
    : [];

  // Same for the log: only this reseller's changes.
  const history = detail
    ? detail.history.filter((h) => !focusReseller || isFocus(h.reseller))
    : [];

  return (
    <DetailModal
      open={open}
      onClose={onClose}
      title={detail?.name || 'Sub-product'}
      subtitle={detail ? `${detail.product} · ${detail.sku ? `SKU ${detail.sku}` : 'no SKU'}` : ''}
      badge={detail && <Badge tone={STATUS_TONE[detail.status] || 'neutral'}>{detail.status}</Badge>}
      footer={(
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className="text-xs text-slate-400 dark:text-slate-500">
            {detailsDirty
              ? 'You have unsaved changes to the details.'
              : 'Reseller prices save individually, from their own row.'}
          </span>
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={onClose}>Close</Button>
            {canEdit && (
              <Button onClick={saveDetails} disabled={savingDetails || !detailsDirty}>
                {savingDetails ? 'Saving…' : 'Save details'}
              </Button>
            )}
          </div>
        </div>
      )}
    >
      {err && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-500/15 dark:text-red-300">{err}</div>}
      {ok && <div className="mb-3 rounded-lg bg-green-50 px-3 py-2 text-sm text-green-700 dark:bg-green-500/15 dark:text-green-300">{ok}</div>}

      {loading || !detail ? <Spinner label="Loading…" /> : (
        <div className="space-y-6">
          {/* ---------- Details ---------- */}
          <section>
            <h4 className="mb-3 text-xs font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">Details</h4>
            {canEdit ? (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-6">
                <div className="col-span-2 sm:col-span-4">
                  <span className={label}>Sub-product name</span>
                  <input dir="auto" value={details.name} onChange={(e) => setDetails({ ...details, name: e.target.value })} className={field} />
                </div>
                <div className="sm:col-span-2">
                  <span className={label}>SKU</span>
                  <input value={details.sku} onChange={(e) => setDetails({ ...details, sku: e.target.value })} placeholder="—" className={field} />
                </div>
                <div className="sm:col-span-2">
                  <span className={label}>Status</span>
                  <select value={details.status} onChange={(e) => setDetails({ ...details, status: e.target.value })} className={field}>
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <span className={label}>Effective date</span>
                  <CalendarPicker
                    value={details.effectiveDate}
                    onChange={(v) => setDetails({ ...details, effectiveDate: v })}
                    portal placeholder="Not set" clearLabel="Clear"
                  />
                </div>
                <div className="sm:col-span-2">
                  <span className={label}>Base price</span>
                  <div className="relative">
                    <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-slate-400">{SYMBOL[details.currency] || '$'}</span>
                    <input
                      type="number" step="0.01" min="0"
                      value={details.basePrice}
                      onChange={(e) => setDetails({ ...details, basePrice: e.target.value })}
                      placeholder="—"
                      className={`${field} no-spin pl-6 text-right tabular-nums`}
                    />
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <span className={label}>Base currency</span>
                  <select value={details.currency} onChange={(e) => setDetails({ ...details, currency: e.target.value })} className={field}>
                    {CURRENCIES.map((c) => <option key={c} value={c}>{SYMBOL[c]} {CURRENCY_LABEL[c] || c}</option>)}
                  </select>
                </div>
                <div className="col-span-2 sm:col-span-4">
                  <span className={label}>Notes</span>
                  <input value={details.notes} onChange={(e) => setDetails({ ...details, notes: e.target.value })} placeholder="—" className={field} />
                </div>
              </div>
            ) : (
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2.5 text-sm sm:grid-cols-3">
                {[
                  ['Product', detail.product],
                  ['SKU', detail.sku || '—'],
                  ['Status', detail.status],
                  ['Effective date', detail.effectiveDate ? formatIsraelDate(detail.effectiveDate) : '—'],
                  ['Base price', moneyLabel(detail.basePrice, detail.currency) || '—'],
                  ['Base currency', detail.currency],
                  ['Notes', detail.notes || '—'],
                ].map(([k, v]) => (
                  <div key={k}>
                    <dt className="text-xs text-slate-400 dark:text-slate-500">{k}</dt>
                    <dd className="text-slate-700 dark:text-slate-200">{v}</dd>
                  </div>
                ))}
              </dl>
            )}
          </section>

          {/* ---------- Reseller pricing ---------- */}
          <section>
            <div className="mb-3 flex items-center justify-between gap-2">
              <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                {focusReseller ? `Pricing for ${focusReseller}` : 'Reseller pricing'}
              </h4>
              {!focusReseller && (
                <span className="text-xs text-slate-400 dark:text-slate-500">{priceRows.length} configured</span>
              )}
            </div>

            <div className="space-y-2.5">
              {priceRows.length === 0 && drafts.length === 0 && (
                <p className="rounded-xl border border-dashed border-slate-300 py-6 text-center text-sm text-slate-400 dark:border-white/15 dark:text-slate-500">
                  {focusReseller ? `${focusReseller} has no price on this sub-product yet.` : 'No reseller pricing configured yet.'}
                </p>
              )}
              {priceRows.map((r) => (
                <PriceRow
                  key={r.reseller} row={r} subProduct={detail}
                  onSaved={(n) => afterWrite(`Price saved for ${n}.`)}
                  onRemoved={(n) => afterWrite(`Pricing removed for ${n}.`)}
                  onError={setErr}
                  onDiscardDraft={() => {}}
                />
              ))}
              {drafts.map((d) => (
                <PriceRow
                  key={`draft-${d.reseller}`} row={d} subProduct={detail}
                  onSaved={(n) => { setDrafts((p) => p.filter((x) => x.reseller !== n)); afterWrite(`Price added for ${n}.`); }}
                  onRemoved={() => {}}
                  onError={setErr}
                  onDiscardDraft={(n) => setDrafts((p) => p.filter((x) => x.reseller !== n))}
                />
              ))}
            </div>

            {canEdit && !focusReseller && (
              <div className="mt-3">
                <Combobox
                  label="Add pricing for a reseller"
                  value=""
                  onChange={addDraft}
                  options={available}
                  placeholder={available.length ? 'Select a reseller…' : 'All resellers are configured'}
                  searchPlaceholder="Search resellers…"
                />
              </div>
            )}
            {canEdit && focusReseller && (
              <p className="mt-2.5 text-xs text-slate-400 dark:text-slate-500">
                Pricing for another reseller? Pick them in <strong className="font-medium">Setting prices for</strong> on
                the page behind, then open this sub-product again.
              </p>
            )}
          </section>

          {/* ---------- History ---------- */}
          <section>
            <h4 className="mb-3 text-xs font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
              Pricing history
            </h4>
            {!history.length ? (
              <p className="text-sm text-slate-400 dark:text-slate-500">
                {focusReseller ? `No changes recorded for ${focusReseller} yet.` : 'No changes recorded yet.'}
              </p>
            ) : (
              <ul className="space-y-2">
                {history.map((h) => {
                  const when = formatIsraelDayTime(h.changedAt);
                  // History rows store the figure, not the currency, so they are
                  // read in the currency that reseller is priced in today. Ample
                  // for telling $ from ₪; it would misread only if a reseller's
                  // currency itself were changed after the fact.
                  const cur = detail.resellerPrices
                    .find((r) => r.reseller.toLowerCase() === h.reseller.toLowerCase())?.currency || detail.currency;
                  const from = moneyLabel(h.oldPrice, cur);
                  const to = moneyLabel(h.newPrice, cur);
                  return (
                    <li key={h.id} className="rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-white/10">
                      <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                        <span className="font-medium text-navy-800 dark:text-slate-100">{h.reseller}</span>
                        <span className="text-xs text-slate-400 dark:text-slate-500">{when.day} {when.time}</span>
                      </div>
                      <div className="mt-0.5 text-slate-600 dark:text-slate-300">
                        {from ? <span className="text-slate-400 line-through dark:text-slate-500">{from}</span> : <span className="text-slate-400 dark:text-slate-500">not configured</span>}
                        <span className="mx-1.5 text-slate-300 dark:text-slate-600">→</span>
                        {to ? <span className="font-semibold">{to}</span> : <span className="text-slate-400 dark:text-slate-500">removed</span>}
                        {h.effectiveFrom && <span className="ml-2 text-xs text-slate-400 dark:text-slate-500">eff. {formatIsraelDate(h.effectiveFrom)}</span>}
                      </div>
                      <div className="mt-0.5 text-xs text-slate-400 dark:text-slate-500">
                        by {h.changedBy}
                        {/* Display names repeat across accounts; the email is the
                            part that actually identifies who made the change. */}
                        {h.changedByEmail && h.changedByEmail !== h.changedBy && (
                          <span className="text-slate-300 dark:text-slate-600"> · {h.changedByEmail}</span>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>
      )}
    </DetailModal>
  );
}
